import { useMemo } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useFinance, usePreferences } from '../../../contexts';
import { api } from '../../../services/api';
import styles from './CashFlowChart.module.css';

type Period = '6M' | '1A' | 'Tudo';

interface TooltipProps {
    active?: boolean;
    payload?: Array<{
        value: number;
        name: string;
        color: string;
    }>;
    label?: string;
    formatCurrency: (value: number) => string;
}

const CustomTooltip = ({ active, payload, label, formatCurrency }: TooltipProps) => {
    if (active && payload && payload.length) {
        return (
            <div className={styles.tooltip}>
                <p className={styles.tooltipLabel}>{label}</p>
                {payload.map((entry, index) => (
                    <p key={index} style={{ color: entry.color }}>
                        {entry.name}: {formatCurrency(entry.value)}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

export function CashFlowChart() {
    const { cashFlowData, applyDateFilter, dateFilter } = useFinance();
    const { formatCurrency, t } = usePreferences();

    const activePeriod = useMemo((): Period => {
        const now = new Date();
        const currentMonth = now.toISOString().slice(0, 7);
        const filterEndMonth = dateFilter.dataFim?.slice(0, 7);

        if (filterEndMonth !== currentMonth) return 'Tudo';

        const expected6M = new Date(now.getFullYear(), now.getMonth() - 5, 1).toISOString().split('T')[0];
        const expected1A = new Date(now.getFullYear() - 1, now.getMonth(), 1).toISOString().split('T')[0];

        if (dateFilter.dataInicio === expected6M) return '6M';
        if (dateFilter.dataInicio === expected1A) return '1A';
        return 'Tudo';
    }, [dateFilter]);

    const periodLabels: Record<Period, string> = {
        '6M': t('chart.ultimos6Meses'),
        '1A': t('chart.ultimoAno'),
        'Tudo': t('chart.todoPeriodo'),
    };

    const periodButtons: { key: Period; label: string }[] = [
        { key: '6M', label: '6M' },
        { key: '1A', label: '1A' },
        { key: 'Tudo', label: t('chart.tudo') },
    ];

    async function handlePeriodChange(period: Period) {
        const now = new Date();
        const dataFim = now.toISOString().split('T')[0];

        if (period === '6M') {
            const inicio = new Date(now.getFullYear(), now.getMonth() - 5, 1);
            applyDateFilter({ dataInicio: inicio.toISOString().split('T')[0], dataFim });
        } else if (period === '1A') {
            const inicio = new Date(now.getFullYear() - 1, now.getMonth(), 1);
            applyDateFilter({ dataInicio: inicio.toISOString().split('T')[0], dataFim });
        } else {
            const anos = await api.getLancamentosAnos();
            const primeiroAno = anos.length > 0 ? Math.min(...anos) : now.getFullYear();
            applyDateFilter({ dataInicio: `${primeiroAno}-01-01`, dataFim });
        }
    }

    const formatYAxis = (value: number) => {
        return `${(value / 1000).toFixed(0)}k`;
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <h3 className={styles.title}>{t('chart.fluxoCaixa')}</h3>
                    <span className={styles.subtitle}>{periodLabels[activePeriod]}</span>
                </div>
                <div className={styles.periodSelector}>
                    {periodButtons.map(({ key, label }) => (
                        <button
                            key={key}
                            className={`${styles.periodBtn} ${activePeriod === key ? styles.active : ''}`}
                            onClick={() => handlePeriodChange(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div className={styles.chartContainer}>
                {cashFlowData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={cashFlowData}
                        margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#b3e6c7" stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#f5b3b3" stopOpacity={0.05} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e5e7eb"
                        />
                        <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: '#9ca3af' }}
                            dy={10}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={formatYAxis}
                            tick={{ fontSize: 12, fill: '#9ca3af' }}
                            dx={-5}
                        />
                        <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                        <Legend
                            verticalAlign="top"
                            align="left"
                            height={36}
                            iconType="circle"
                            iconSize={8}
                            formatter={(value) => (
                                <span style={{ color: '#6b7280', fontSize: '12px' }}>
                                    {value}
                                </span>
                            )}
                        />
                        <Area
                            type="monotone"
                            dataKey="income"
                            name={t('chart.receitas')}
                            stroke="#159243"
                            strokeWidth={2}
                            fill="url(#incomeGradient)"
                            animationDuration={1500}
                            animationEasing="ease-in-out"
                        />
                        <Area
                            type="monotone"
                            dataKey="expense"
                            name={t('chart.despesas')}
                            stroke="#dc2626"
                            strokeWidth={2}
                            fill="url(#expenseGradient)"
                            animationDuration={1500}
                            animationEasing="ease-in-out"
                            animationBegin={300}
                        />
                    </AreaChart>
                </ResponsiveContainer>
                ) : (
                    <div className={styles.empty}>{t('chart.semDadosFluxo')}</div>
                )}
            </div>
        </div>
    );
}
